import { MatrixType, ProductGender, ProductMainGroups, type WarehouseThresholds } from '@onrunning/backstage-models';
import { computed, ref, toValue, type Ref } from 'vue';

import { genderLetterMap } from '@/constants/matrix.constants';
import { CARRY_OVER_UNAVAILABLE_DAYS } from '@/constants/order.constants';
import { utilsMixins } from '@/mixins/utils.mixins';
import type { MatrixProduct } from '@/models/matrix-product.model';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { Availability } from '@/types/matrix.enums';
import type {
  Fabric,
  GroupedMatrix,
  MatrixConfig,
  ProductGroup,
  ProductMatrixApiResponse,
} from '@/types/matrix.interface';
import { addDateDuration, toISOString } from '@/utils/temporalUtils';
import { useFabricValidation } from './useFabricValidation';
import { useMatrixProduct } from './useMatrixProduct';
import { getOrCreate } from './utils';

export function useGroupedMatrix(productMatrix: Ref<ProductMatrixApiResponse | null>, matrixConfig: Ref<MatrixConfig>) {
  const productMatrixStore = useProductMatrixStore();
  const matrixProduct = useMatrixProduct();
  const fabricValidations = useFabricValidation(ref(matrixConfig.value?.matrixType));

  const getPreOrderEarliestRequestedDate = (launchDate: string): string => {
    if (
      fabricValidations.isCarryOverDisabled(
        launchDate,
        matrixConfig.value?.seasonStart, // use season start as order date for carry over calculation
        matrixConfig.value?.seasonStart,
      )
    ) {
      if (matrixConfig.value?.seasonStart) {
        return toISOString(
          addDateDuration(matrixConfig.value.seasonStart, {
            days: CARRY_OVER_UNAVAILABLE_DAYS,
          }),
        );
      }
    }

    return toISOString(launchDate);
  };

  const groupedMatrix = computed<Map<ProductMainGroups, GroupedMatrix>>(() => {
    if (!productMatrix.value) {
      return new Map();
    }

    const { warehouse, products, sizeGroups } = productMatrix.value;
    const sizesByProductGroup = new Map(
      sizeGroups.map((group) => [group.sizeGroup.toLowerCase(), new Map(group.sizes.map((size) => [size.id, size]))]),
    );

    const groupedMatrixMap = new Map();
    const matrixProducts: Record<string, MatrixProduct> = {};

    for (const product of products) {
      // Gender
      const groupGenderKey =
        product.mainGroup === ProductMainGroups.accessories
          ? `${product.mainGroup}-${genderLetterMap[ProductGender.unisex]}`
          : `${product.mainGroup}-${genderLetterMap[product.gender]}`;
      const groupGender: GroupedMatrix = getOrCreate(groupedMatrixMap, groupGenderKey, () => ({
        id: groupGenderKey,
        name: product.gender,
        productGroups: new Map(),
      }));

      // Product Group
      const productGroupSizes = sizesByProductGroup.get(product.sizeGroup.toLowerCase()) ?? new Map();
      const productGroupKey = `${product.gender}-${product.productGroupName}`;
      const productGroup: ProductGroup = getOrCreate(groupGender.productGroups, productGroupKey, () => ({
        id: productGroupKey,
        name: product.productGroupName,
        sizes: new Map(),
        fabrics: new Map(),
      }));

      // Sizes
      const groupSize = productGroupSizes.get(product.sizeId);
      if (groupSize) {
        getOrCreate(productGroup.sizes, product.sizeId, () => productGroupSizes.get(product.sizeId));
      }

      // Fabric
      const disabledReason = fabricValidations.getFabricDisabledReason(
        product.launchDate,
        matrixConfig.value?.orderDate,
        matrixConfig.value?.seasonStart,
      );

      const fabric: Fabric = getOrCreate(productGroup.fabrics, product.productGroupNumber, () => ({
        id: product.productGroupNumber,
        name: product.color,
        launchDate: product.launchDate,
        products: {},
        disabled: !!disabledReason,
        disabledReason: disabledReason,
        imageUrl: utilsMixins.getBoxImageProxyUrl(product.picUrl),
        description: product.productGroupNumber,
        targetedAssortments: product?.targetedAssortments,
      }));

      if (matrixProduct.matrixType.value === MatrixType.preorder) {
        fabric.preOrderEarliestRequestedDate = getPreOrderEarliestRequestedDate(product.launchDate);
      }

      if (showAvailabilityLabel.value) {
        product.availability = getAvailability(product.availableQuantity, warehouse.thresholds);
      }
      product.disabled = fabric.disabled || product.availability === Availability.outOfStock;

      fabric.products[product.sizeId] = product;
      matrixProducts[product.productId] = product;
    }

    productMatrixStore.setProducts(matrixProducts);

    return groupedMatrixMap;
  });

  const showAvailabilityLabel = computed<boolean>(() => {
    const config = toValue(matrixConfig);

    return (
      config.matrixType !== MatrixType.warranty &&
      config.matrixType !== MatrixType.return &&
      config.matrixType !== MatrixType.preorder
    );
  });

  const getAvailability = (availableQuantity: number, threshold: WarehouseThresholds) => {
    switch (true) {
      case availableQuantity <= threshold.outOfStock:
        return Availability.outOfStock;
      case availableQuantity <= threshold.veryLowStock:
        return Availability.veryLowStock;
      case availableQuantity <= threshold.lowStock:
        return Availability.lowStock;
      default:
        return Availability.inStock;
    }
  };

  return { groupedMatrix, showAvailabilityLabel };
}
